import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BlockNoteSchema, defaultBlockSpecs, defaultStyleSpecs } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { FormattingToolbar, useCreateBlockNote } from '@blocknote/react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import MarkdownImporter from '@components/Markdown/MarkdownImporter';
import type { ModalComponentProps } from '@components/modals/constants';
import { useClinicians } from '@features/organizations';
import { useSubjectLanguageVariants } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { parseNoteToBlocks, serializeBlocks } from '@utils/blocknote';
import { useSnackbar } from 'notistack';

import {
  useCreateTreatmentPlanMutation,
  useGetTreatmentPlanQuery,
  useMarkTreatmentPlanReviewedMutation,
  useSetTreatmentPlanReviewDateMutation,
  useUpdateTreatmentPlanMutation,
} from '../store/api';
import { daysUntil, formatReviewDate } from '../utils/reviewDate';

import type { TreatmentPlanEditModalProps } from './PatientTreatmentPlanEditModal.constants';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

const {
  codeBlock: _codeBlock,
  audio: _audio,
  video: _video,
  image: _image,
  file: _file,
  ...blockSpecs
} = defaultBlockSpecs;
const { code: _code, ...styleSpecs } = defaultStyleSpecs;
const treatmentPlanSchema = BlockNoteSchema.create({ blockSpecs, styleSpecs });

export const PatientTreatmentPlanEditModal: React.FC<ModalComponentProps> = ({
  modalProps,
  closeModal,
}) => {
  const {
    patientId,
    source = 'unknown',
  } = modalProps as TreatmentPlanEditModalProps;

  const { singular } = useSubjectLanguageVariants();
  const { patientTreatmentPlan: treatmentPlanTracking } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const { data: treatmentPlan, isLoading: isFetchingPlan } =
    useGetTreatmentPlanQuery({ patientId });
  const { clinicians } = useClinicians();

  const [treatmentPlanId, setTreatmentPlanId] = useState<string | null>(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [datePopoverAnchor, setDatePopoverAnchor] = useState<HTMLElement | null>(null);
  const [dateDraft, setDateDraft] = useState<string>('');
  const pendingMarkdownRef = useRef<string | null>(null);
  const isLoadingContent = useRef(false);
  const triggerAutoSaveRef = useRef<((md: string) => void) | null>(null);

  const editor = useCreateBlockNote({ schema: treatmentPlanSchema });

  const [createPlan] = useCreateTreatmentPlanMutation();
  const [updatePlan] = useUpdateTreatmentPlanMutation();
  const [markReviewed, { isLoading: isMarkingReviewed }] =
    useMarkTreatmentPlanReviewedMutation();
  const [setReviewDate, { isLoading: isSettingReviewDate }] =
    useSetTreatmentPlanReviewDateMutation();

  const initializedRef = useRef(false);
  const hasTrackedOpenedRef = useRef(false);

  const mustBeUpdatedBy = treatmentPlan?.must_be_updated_by ?? null;
  const updatedAt = treatmentPlan?.updated_at ?? null;
  const updatedByUserId = treatmentPlan?.updated_by ?? null;

  const updatedByName = useMemo(() => {
    if (!updatedByUserId) return null;
    return clinicians.find(c => c.userId === updatedByUserId)?.name ?? null;
  }, [clinicians, updatedByUserId]);

  const reviewDays = daysUntil(mustBeUpdatedBy);
  const reviewBadgeColor: 'default' | 'warning' | 'error' =
    reviewDays === null
      ? 'default'
      : reviewDays < 0
      ? 'error'
      : reviewDays <= 30
      ? 'warning'
      : 'default';
  const reviewBadgeLabel = mustBeUpdatedBy
    ? reviewDays !== null && reviewDays < 0
      ? `Overdue · ${formatReviewDate(mustBeUpdatedBy)}`
      : `Review by ${formatReviewDate(mustBeUpdatedBy)}`
    : null;

  useEffect(() => {
    if (isFetchingPlan || initializedRef.current) return;
    initializedRef.current = true;

    const loadContent = async () => {
      isLoadingContent.current = true;
      const stored = treatmentPlan?.markdown_content || '';
      const blocks = await parseNoteToBlocks(editor, stored);
      if (blocks.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        editor.replaceBlocks(editor.document, blocks);
      }
      setTreatmentPlanId(treatmentPlan?.treatment_plan_id || null);
      setHasLocalChanges(false);
      pendingMarkdownRef.current = null;
      requestAnimationFrame(() => {
        isLoadingContent.current = false;
      });
    };
    loadContent();
  }, [isFetchingPlan, treatmentPlan, editor]);

  // Fire the open event once per mount, after the plan has loaded so
  // item_count reflects whether the patient has an existing plan. Tracking
  // lives here (not at the click site) so every entry point — context bar,
  // patient-page server-UI tiles, etc. — produces a matching open/close pair.
  useEffect(() => {
    if (isFetchingPlan || hasTrackedOpenedRef.current) return;
    hasTrackedOpenedRef.current = true;
    treatmentPlanTracking.opened({
      patient_id: patientId,
      item_count: treatmentPlan ? 1 : 0,
      source,
    });
  }, [isFetchingPlan, treatmentPlan, patientId, source, treatmentPlanTracking]);

  const performSave = useCallback(
    async (markdownToSave: string) => {
      try {
        const response = treatmentPlanId
          ? await updatePlan({
              patientId,
              updateTreatmentPlanRequest: { markdown_content: markdownToSave },
            }).unwrap()
          : await createPlan({
              patientId,
              createTreatmentPlanRequest: { markdown_content: markdownToSave },
            }).unwrap();
        const newId = response.treatment_plan?.treatment_plan_id;
        if (newId && newId !== treatmentPlanId) {
          setTreatmentPlanId(newId);
        }
        setHasLocalChanges(false);
        pendingMarkdownRef.current = null;
      } catch (error) {
        console.error('Failed to save treatment plan:', error);
        treatmentPlanTracking.saveFailed({
          patient_id: patientId,
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [patientId, treatmentPlanId, createPlan, updatePlan, treatmentPlanTracking, source],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt } = useAutoSave({
    onSave: performSave,
    debounceMs: 2000,
    enabled: hasLocalChanges,
    flushOnUnmount: true,
  });
  triggerAutoSaveRef.current = triggerAutoSave;

  const handleEditorChange = useCallback(() => {
    if (isLoadingContent.current) return;
    const serialized = serializeBlocks(editor.document);
    setHasLocalChanges(true);
    pendingMarkdownRef.current = serialized;
    triggerAutoSave(serialized);
  }, [editor, triggerAutoSave]);

  const handleBlur = useCallback(() => {
    if (pendingMarkdownRef.current !== null) {
      saveImmediately(pendingMarkdownRef.current);
      pendingMarkdownRef.current = null;
    }
  }, [saveImmediately]);

  const handleClose = useCallback(() => {
    if (status === 'saving') return;
    treatmentPlanTracking.closed({
      patient_id: patientId,
      had_changes: hasLocalChanges,
      source,
    });
    if (hasLocalChanges && pendingMarkdownRef.current !== null) {
      saveImmediately(pendingMarkdownRef.current);
      pendingMarkdownRef.current = null;
    }
    closeModal();
  }, [status, hasLocalChanges, saveImmediately, closeModal, patientId, source, treatmentPlanTracking]);

  const handleImport = useCallback(() => {
    treatmentPlanTracking.importClicked({
      patient_id: patientId,
      source,
    });
    setImportOpen(true);
  }, [patientId, source, treatmentPlanTracking]);

  const handleImportSuccess = useCallback(
    async (markdown: string) => {
      try {
        const blocks = await editor.tryParseMarkdownToBlocks(markdown);
        editor.replaceBlocks(editor.document, blocks);
        const serialized = serializeBlocks(editor.document);
        setHasLocalChanges(true);
        pendingMarkdownRef.current = serialized;
        triggerAutoSaveRef.current?.(serialized);
        setImportOpen(false);
        treatmentPlanTracking.importCompleted({
          patient_id: patientId,
          source,
        });
      } catch (error) {
        treatmentPlanTracking.importFailed({
          patient_id: patientId,
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
      }
    },
    [editor, patientId, source, treatmentPlanTracking],
  );

  const handleMarkReviewed = useCallback(async () => {
    try {
      const result = await markReviewed({ patientId }).unwrap();
      enqueueSnackbar(
        `Marked reviewed. Next review by ${formatReviewDate(result.must_be_updated_by) ?? 'soon'}.`,
        { variant: 'success' },
      );
    } catch (error) {
      enqueueSnackbar('Could not mark as reviewed', { variant: 'error' });
      console.error('Failed to mark treatment plan reviewed', error);
    }
  }, [markReviewed, patientId, enqueueSnackbar]);

  const handleOpenDatePopover = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setDateDraft(mustBeUpdatedBy ?? '');
      setDatePopoverAnchor(event.currentTarget);
    },
    [mustBeUpdatedBy],
  );

  const handleSaveReviewDate = useCallback(async () => {
    if (!dateDraft) {
      setDatePopoverAnchor(null);
      return;
    }
    try {
      await setReviewDate({
        patientId,
        setTreatmentPlanReviewDateRequest: { must_be_updated_by: dateDraft },
      }).unwrap();
      enqueueSnackbar(`Review date set to ${formatReviewDate(dateDraft)}.`, {
        variant: 'success',
      });
      setDatePopoverAnchor(null);
    } catch (error) {
      enqueueSnackbar('Could not update review date', { variant: 'error' });
      console.error('Failed to set treatment plan review date', error);
    }
  }, [dateDraft, setReviewDate, patientId, enqueueSnackbar]);

  const hasExistingPlan = !!treatmentPlanId;

  return (
    <>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Treatment Plan
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleImport}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8125rem',
              }}
            >
              Import
            </Button>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              disabled={status === 'saving'}
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {hasExistingPlan && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              mt: 1,
              color: 'text.secondary',
            }}
          >
            {reviewBadgeLabel && (
              <Chip
                size="small"
                color={reviewBadgeColor === 'default' ? undefined : reviewBadgeColor}
                variant={reviewBadgeColor === 'default' ? 'outlined' : 'filled'}
                label={reviewBadgeLabel}
                icon={<EventOutlinedIcon sx={{ fontSize: 14 }} />}
              />
            )}
            {(updatedAt || updatedByName) && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {updatedAt ? `Last updated ${formatReviewDate(updatedAt)}` : null}
                {updatedAt && updatedByName ? ' by ' : null}
                {updatedByName ?? null}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<DoneAllOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={handleMarkReviewed}
              disabled={isMarkingReviewed}
              sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
            >
              Mark reviewed
            </Button>
            <Button
              size="small"
              onClick={handleOpenDatePopover}
              disabled={isSettingReviewDate}
              sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
            >
              Change date
            </Button>
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box
          onBlur={handleBlur}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            '& .bn-container': {
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column-reverse',
              height: '100%',
              overflow: 'auto !important',
            },
            '& .bn-editor': {
              flex: 1,
              padding: '16px',
              overflow: 'visible !important',
            },
          }}
        >
          <BlockNoteView
            editor={editor}
            editable
            theme="light"
            onChange={handleEditorChange}
            formattingToolbar={false}
            sideMenu={false}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '& .bn-formatting-toolbar': {
                  border: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                  padding: '4px 16px',
                  minHeight: 36,
                },
              }}
            >
              <FormattingToolbar />
            </Box>
          </BlockNoteView>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <AutosaveStatus
          status={status}
          isDirty={hasLocalChanges}
          lastSavedAt={lastSavedAt}
          verbose
        />
        <Button
          onClick={handleClose}
          variant="contained"
          disabled={status === 'saving' || hasLocalChanges}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Done
        </Button>
      </DialogActions>

      <Popover
        open={!!datePopoverAnchor}
        anchorEl={datePopoverAnchor}
        onClose={() => setDatePopoverAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 240 }}>
          <Typography variant="subtitle2">Set review-by date</Typography>
          <TextField
            type="date"
            size="small"
            value={dateDraft}
            onChange={e => setDateDraft(e.target.value)}
            fullWidth
           slotProps={{ inputLabel: { shrink: true } }}/>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={() => setDatePopoverAnchor(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveReviewDate}
              disabled={!dateDraft || isSettingReviewDate}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>

      <MarkdownImporter
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImportSuccess={handleImportSuccess}
        endpoint="/treatment_plan"
        contentFieldName="text"
        additionalParams={{ patient_id: patientId }}
        title="Import Treatment Plan"
        description="Paste text or drag a file to import a treatment plan. Supported file types: .pdf, .docx, .jpg, .jpeg, .png"
        acceptedFileTypes=".pdf,.docx,.jpg,.jpeg,.png"
        warningText={
          treatmentPlan && (treatmentPlan.markdown_content ?? '').trim().length > 0
            ? `Note: This import will overwrite the existing treatment plan for this ${singular}.`
            : undefined
        }
      />
    </>
  );
};

PatientTreatmentPlanEditModal.displayName = 'PatientTreatmentPlanEditModal';
